/**
 * @module AuroraCustomer
 * @description Singleton module that manages Aurora Customers.
 */
const AuroraCustomer = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} settings
   * @description The initial settings for the singleton object.
   */
  const settings = {
    name: "aurora-customer.js",
    customer_id: window.theme.customer?.id || 0,
    debugging: true,
    timestamp: document.querySelector("meta[name='timestamp']").getAttribute("content") || "",
    signature: document.querySelector("meta[name='token']").getAttribute("content") || "",
    gram: {
      prescriptionLimit: 0,
      current: 0,
      withinLimit: false,
    },
  };

  /**
   * An object representing a cookie data.
   *
   * @private
   * @type {object} cookie
   * @description The initial settings for the cookie data.
   */
  const cookie = {
    prescriptionPopup: {
      name: "prescription-popup",
      duration: 30,
    },
    productPreferenceTracking: {
      name: "product-preference-tracking",
      maxAge: {
        days: 365,
      },
    },
  };

  /**
   * An object representing a customer data.
   *
   * @type {object} CUSTOMER
   * @description This object contains details about a customer which
   * has been extracted from the Shopify liquid object 'customer', passed
   * to the global window object and then extacted here.
   */
  const CUSTOMER = window.theme?.customer || {};

  /**
   * Set the product preference.
   *
   * @function setProductPreference
   * @param {string} productPreference - The product preference, either "like" or "dislike".
   * @param {string} productID - The product ID as a string.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function setProductPreference(productPreference = "", productID = "") {
    const secondsPerDay = 86400;
    const maxAge = 365 * secondsPerDay;
    const customerID = getCustomerID();
    const customerCookieName = `aur-product-preference--${customerID}`;
    const cookieData = JSON.parse(AuroraUtils.getCookie(customerCookieName) || "{}");
    const { likes = [], dislikes = [] } = cookieData;

    switch (productPreference) {
      case "dislike": {
        cookieData.dislikes = AuroraUtils.uniqueArray(dislikes, productID);
        cookieData.likes = likes.filter((id) => id !== productID);
        break;
      }
      case "like": {
        cookieData.likes = AuroraUtils.uniqueArray(likes, productID);
        cookieData.dislikes = dislikes.filter((id) => id !== productID);
        break;
      }
    }

    AuroraUtils.setCookie(customerCookieName, JSON.stringify(cookieData), { secure: true, "max-age": maxAge });
  }

  /**
   * Set customer preference of product on input "change" event.
   *
   * @function setProductPreferenceOnInputChange
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function setProductPreferenceOnInputChange() {
    document.querySelectorAll(".js--product-preference-tracking-input").forEach((input) => {
      input.addEventListener("change", () => {
        const productID = input.name.replace("preference--", "");
        const productPreference = input.value;

        setProductPreference(productPreference, productID);
      });
    });
  }

  /**
   * Fetches customer data from MCRM.
   *
   * @async
   * @function fetchCustomerDataFromMCRM
   * @returns {Promise<Object[]>} A promise that resolves to an object of user data.
   *
   * @author Brenden Mersey
   */
  async function fetchCustomerDataFromMCRM() {
    const accessToken = getLoginRadiusToken();
    const customerID = getCustomerID();
    const signature = getSignature();
    const timestamp = getTimestamp();

    return await AuroraServices.getCustomerByID(customerID, accessToken, timestamp, signature);
  }

  /**
   * Get gram allowance remaining.
   *
   * @function getAllowanceRemaining
   * @param {Object} customer - A customer object.
   * @returns {number} The customer's remaining allowance.
   *
   * @author Brenden Mersey
   */
  function getAllowanceRemaining(customer = {}) {
    return customer.allowance_remaining;
  }

  /**
   * Get customer ID.
   *
   * @function getCustomerID
   * @returns {number} The customer's ID.
   *
   * @author Brenden Mersey
   */
  function getCustomerID() {
    return CUSTOMER.id;
  }

  /**
   * Get customer.
   *
   * @function getCustomer
   * @returns {Object} The customer object.
   *
   * @author Brenden Mersey
   */
  function getCustomer() {
    return CUSTOMER;
  }

  /**
   * Get Login Radius token.
   *
   * @function getLoginRadiusToken
   * @returns {string} The Login Radius token.
   *
   * @author Brenden Mersey
   */
  function getLoginRadiusToken() {
    return localStorage.getItem("LRTokenKey");
  }

  /**
   * Get total gram allowance for the order based on the customer.
   *
   * @function getOrderAllowanceTotal
   * @param {Object} customer - A customer object.
   * @returns {number} The total gram allowance for the order.
   *
   * @author Brenden Mersey
   */
  function getOrderAllowanceTotal(customer = {}) {
    const maxAllowance = getMaximumOrderAllowance(customer);
    return maxAllowance <= 150 ? maxAllowance : 150;
  }

  /**
   * Get total gram allowance for the order based on the customer prescription.
   *
   * @function getPrescriptionOrderLimit
   * @param {Object} customer - A customer object.
   * @returns {number} The total gram allowance for the order based off the customer prescription.
   *
   * @author Brenden Mersey
   */
  function getPrescriptionOrderLimit(customer = {}) {
    // Legacy version of this function below...
    // https://bitbucket.org/auroracannabis/aurora-medical-store/src/f2d537bca0098dca042a383a713b48571cc81a17/scripts/partials/atc-logic/validations/validate-allowance.js?at=main#validate-allowance.js-4

    const allowanceGramsPerDay = customer.allowance_grams_per_day;
    const allowanceRemaining = customer.allowance_remaining;
    const thirtyDayAllowance = allowanceGramsPerDay * 30;

    if (allowanceRemaining < 150 && allowanceRemaining < thirtyDayAllowance) {
      return allowanceRemaining;
    }

    return thirtyDayAllowance > 150 ? 150 : thirtyDayAllowance;
  }

  /**
   * Get total gram allowance from cached customer object.
   *
   * @function getPrescriptionLimit
   * @returns {number} The total gram allowance for the order based off the customer prescription.
   *
   * @author Brenden Mersey
   */
  function getPrescriptionLimit() {
    return settings.gram.prescriptionLimit;
  }

  /**
   * Get signature from website.
   *
   * @function getSignature
   * @returns {string} The signature from the website.
   *
   * @author Brenden Mersey
   */
  function getSignature() {
    return document.querySelector("meta[name='token']").getAttribute("content") || "";
  }

  /**
   * Get timestamp from website.
   *
   * @function getTimestamp
   * @returns {string} The timestamp from the website.
   *
   * @author Brenden Mersey
   */
  function getTimestamp() {
    return document.querySelector("meta[name='timestamp']").getAttribute("content") || "";
  }

  /**
   * Get current grams from cached settings object.
   *
   * @function getCurrentGrams
   * @returns {number} The timestamp from the website.
   *
   * @author Brenden Mersey
   */
  function getCurrentGrams() {
    return settings.gram.current;
  }

  /**
   * Get the percentage value of the gram limit.
   *
   * @function getGramLimitPercentage
   * @returns {number} The percentage value of the gram limit.
   *
   * @author Brenden Mersey
   */
  function getGramLimitPercentage(limit = 0, current = 0) {
    return ((current / limit) * 100).toFixed(2);
  }

  /**
   * Get the maximum order gram allowance from a customer object.
   *
   * @function getMaximumOrderAllowance
   * @param {Object} customer - A customer object.
   * @returns {number} The maximum order gram allowance.
   *
   * @author Brenden Mersey
   */
  function getMaximumOrderAllowance(customer = {}) {
    const maxAllowance = parseFloat(
      customer.allowanceRemaining <= customer.monthlyAllowance ? customer.allowanceRemaining : customer.monthlyAllowance
    );
    return isNaN(maxAllowance) ? 100.0 : maxAllowance;
  }

  /**
   * Set the current grams to the cached settings object.
   *
   * @function setCurrentGrams
   * @param {number} grams - The number of grams to set.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function setCurrentGrams(grams = 0) {
    settings.gram.current = grams;
  }

  /**
   * Set the current grams to the cached settings object.
   *
   * @function setCustomerToLocalStorage
   * @param {Object} customer - The customer object to .
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function setCustomerToLocalStorage(customer = {}) {
    localStorage.setItem("aur_customer", JSON.stringify(customer));
  }

  /**
   * Set the current prescription limit to cached settings object.
   *
   * @function setPrescriptionLimit
   * @param {number} limit - The customer prescription limit.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function setPrescriptionLimit(limit = 0) {
    settings.gram.prescriptionLimit = limit;
  }

  /**
   * Show product preferences on products.
   *
   * @function showProductPreferenceOnProduct
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function showProductPreferenceOnProduct() {
    const customerID = getCustomerID();
    const customerCookieName = `aur-product-preference--${customerID}`;
    const { likes = [], dislikes = [] } = JSON.parse(AuroraUtils.getCookie(customerCookieName) || "{}");

    (document.querySelectorAll(`.js--product-preference-tracking-input`) || []).forEach((input) => {
      const productID = input.name.replace("preference--", "");
      const productPreference = input.value;

      likes.forEach((id) => {
        if (productID === id && "like" === productPreference) input.checked = true;
      });

      dislikes.forEach((id) => {
        if (productID === id && "dislike" === productPreference) input.checked = true;
      });
    });
  }

  /**
   * Updates the current grams from the sum of the cart items.
   *
   * @function withinGramLimit
   * @returns {boolean} Returns `true` if the customer's current gram total is less than or equal to their prescription limit, otherwise returns `false`.
   *
   * @author Brenden Mersey
   */
  function withinGramLimit() {
    return settings.gram.current <= settings.gram.prescriptionLimit ? true : false;
  }

  /**
   * Updates the current grams from the sum of the cart items.
   *
   * @function showPrescriptionPopup
   * @returns {boolean} Returns `true` if the customer is logged, otherwise returns `false`.
   *
   * @author Brenden Mersey
   */
  function showPrescriptionPopup() {
    if (CUSTOMER.is_logged_in) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Detect access token in the URL and set it in the localStorage 'accHash' variable.
   * Run ssoLogin.
   * It's used to detect 'Shop as' action from the MCRM
   *
   * @function detectUrlAccessToken
   * @returns {void} This function does not return anything.
   *
   * @author Halina Tretsiakova
   */
  async function detectUrlAccessToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get("access_token");

    // If an access token has been passed, then the request is a Shop As request.
    if (accessToken) {
      localStorage.setItem("accHash", sha256(accessToken));
      await AuroraServices.ssoLogin(accessToken, exchangeMultipassToken);
    }
  }

  /**
   * Initializes the Aurora Customer singleton object.
   *
   * @async
   * @function init
   * @description This function asynchronously fetches user data from MCRM and then updates
   * the local cached version of the user within the singleton object.
   * @returns {Object} The customer object.
   *
   * @author Brenden Mersey
   */
  function init() {
    if (settings.debugging) console.log(`${settings.name} :: Start`);

    CUSTOMER.LRTokenKey = getLoginRadiusToken();
    CUSTOMER.shopify_address = window.theme.customer?.default_address || "";
    CUSTOMER.shopify_id = window.theme.customer?.id || 0;
    CUSTOMER.client_id = window.theme.customer?.client_id || 0;
    CUSTOMER.limits = {
      // Restrictions
      isAllowedExtract: window.theme.customer.restriction_oil_restricted === "true",
      isAllowedDried: window.theme.customer.restriction_dry_restricted === "true",
      maxDriedTHC: window.theme.customer.restriction_dry_max || 1000,
      maxExtractTHC: window.theme.customer.restriction_oil_max || 1000,
      // Client data
      expirationDate: new Date(window.theme.customer.med_doc_expiry_date),
      province: window.theme.customer.provice_code, // might cause hiccup without lowercase
      // Allowance
      allowanceTotal: window.theme.customer.allowance_total,
      allowanceRemaining: window.theme.customer.allowance_remaining,
      monthlyAllowance: window.theme.customer.allowance_grams_per_day * 30, // not exact, but that's been ok'd validated by Ahmad + David
      maximumOrderAllowance: getMaximumOrderAllowance(CUSTOMER),
      orderAllowanceTotal: getOrderAllowanceTotal(CUSTOMER),
      maximumPeriodCoverage: window.theme.customer.insurance_maximum_period_coverage
        ? parseFloat(window.theme.customer.insurance_maximum_period_coverage)
        : null,
      coverageRemaining: window.theme.customer.insurance_remaining_coverage ? parseFloat(window.theme.customer.insurance_remaining_coverage) : null,
    };

    if (getCustomerID()) {
      setProductPreferenceOnInputChange();
      showProductPreferenceOnProduct();
    }

    if (settings.debugging) console.log(`${settings.name} :: Complete`);

    return CUSTOMER;
  }

  return {
    getAllowanceRemaining,
    getCustomer,
    getGramLimitPercentage,
    getMaximumOrderAllowance,
    getOrderAllowanceTotal,
    getPrescriptionOrderLimit,
    info: CUSTOMER,
    init,
    settings,
    withinGramLimit,
  };
})();
